/* oxlint-disable react/only-export-components */
import { cn } from '@src/lib/utils';

import { css } from '@emotion/css';
import {
    ComponentPropsWithoutRef,
    forwardRef,
    useCallback,
    useMemo,
    useRef,
    type RefObject,
} from 'react';
import 'react-quill/dist/quill.bubble.css';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';

const QUILL_MODULES = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        ['link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote'],
    ],
};

/** Без `size` / `header` — иначе в тулбаре появляется picker «Normal», «Normal 1» и т.д. */
const QUILL_FORMATS = [
    'bold',
    'italic',
    'underline',
    'link',
    'list',
    'blockquote',
];

export interface HtmlEditorFieldProps extends Omit<
    ComponentPropsWithoutRef<'div'>,
    'onChange'
> {
    value?: string;
    onChange?: (v: string) => unknown;
    maxPlainTextLength?: number;
    onLimitExceeded?: (exceeded: boolean) => void;
    placeholder?: string;
    readOnly?: boolean;
    disabled?: boolean;
    isShowOnlyHtml?: boolean;
}

const isEmptyQuillHtml = (html: string | undefined) => {
    const value = html ?? '';
    return (
        value === '' ||
        value === '<p><br></p>' ||
        value === '<p></p>' ||
        value === '<br>'
    );
};

export const useHtmlEditorChange = ({
    disabled,
    maxPlainTextLength,
    onChange,
    onLimitExceeded,
    value,
}: {
    disabled: boolean;
    maxPlainTextLength?: number;
    onChange?: (v: string) => unknown;
    onLimitExceeded?: (exceeded: boolean) => void;
    value?: string;
}) => {
    const lastExceededRef = useRef<boolean | undefined>(undefined);

    return useCallback(
        (
            content: string,
            _delta?: unknown,
            source?: unknown,
            editor?: { getText: () => string },
        ) => {
            if (disabled) return;
            if (source === 'api' || source === 'silent') return;
            if (
                content === (value ?? '') ||
                (isEmptyQuillHtml(content) && isEmptyQuillHtml(value))
            ) {
                return;
            }
            if (maxPlainTextLength !== undefined && editor) {
                const plainText = editor.getText().replace(/\n$/, '');
                const exceeded = plainText.length > maxPlainTextLength;
                if (lastExceededRef.current !== exceeded) {
                    lastExceededRef.current = exceeded;
                    onLimitExceeded?.(exceeded);
                }
                if (exceeded) return;
            }
            onChange?.(content);
        },
        [disabled, maxPlainTextLength, onChange, onLimitExceeded, value],
    );
};

export const HtmlEditorField = forwardRef<HTMLDivElement, HtmlEditorFieldProps>(
    function HtmlEditorField(
        {
            value: valueProp = '',
            onChange: onChangeProp,
            maxPlainTextLength,
            onLimitExceeded,
            placeholder,
            disabled = false,
            readOnly = false,
            className,
            isShowOnlyHtml = false,
            ...props
        },
        ref,
    ) {
        const quillRef = useRef<ReactQuill | null>(null);
        const modules = useMemo(() => QUILL_MODULES, []);
        const handleChange = useHtmlEditorChange({
            disabled,
            maxPlainTextLength,
            onChange: onChangeProp,
            onLimitExceeded,
            value: valueProp,
        });

        if (isShowOnlyHtml) {
            return (
                <div ref={ref} key={'bubble'} className={className} {...props}>
                    <div
                        className={quillWrapperStyle}
                        onMouseDown={(e) => {
                            if (
                                (e.target as HTMLElement).closest?.(
                                    '.ql-toolbar',
                                )
                            ) {
                                e.preventDefault();
                            }
                        }}
                    >
                        <div
                            className={css`
                                .ql-editor {
                                    padding: 0 !important;
                                    color: var(--color-simple) !important;
                                }
                            `}
                        >
                            <ReactQuill
                                ref={quillRef}
                                className={quillEditorStyle}
                                formats={QUILL_FORMATS}
                                modules={modules}
                                placeholder={placeholder}
                                readOnly={true}
                                theme='bubble'
                                value={valueProp ?? '-'}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                }}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div ref={ref} key={'snow'} className={className} {...props}>
                <div
                    className={cn(
                        quillWrapperStyle,
                        disabled && quillDisabledStyle,
                    )}
                    onMouseDown={(e) => {
                        if (
                            (e.target as HTMLElement).closest?.('.ql-toolbar')
                        ) {
                            e.preventDefault();
                        }
                    }}
                >
                    <ReactQuill
                        ref={quillRef}
                        formats={QUILL_FORMATS}
                        modules={modules}
                        placeholder={placeholder}
                        readOnly={readOnly || disabled}
                        theme='snow'
                        value={valueProp ?? ''}
                        className={cn(
                            quillEditorStyle,
                            disabled && quillEditorDisabledStyle,
                        )}
                        onChange={handleChange}
                    />
                </div>
            </div>
        );
    },
);

HtmlEditorField.displayName = 'HtmlEditorField';

export const quillWrapperStyle = css`
    .ql-container {
        border-radius: 0 0 12px 12px;
        border-color: var(--color-editor-divider);
        font-size: 14px;
        min-height: 7em;
        height: auto !important;
        width: 100%;
        max-width: 100%;
    }

    .ql-editor {
        min-height: 7em;
        padding: 12px 15px;
        height: auto !important;
        overflow-y: visible;
        color: var(--color-card-text);
        overflow-x: hidden;
        word-break: break-word;
        overflow-wrap: anywhere;
        white-space: pre-wrap;
    }

    .ql-toolbar {
        border-radius: 12px 12px 0 0;
        border-color: var(--color-editor-divider);
        background: var(--color-body);
    }

    .ql-toolbar .ql-formats + .ql-formats {
        border-left: 1px solid var(--color-editor-divider);
        margin-left: 0 !important;
        padding-left: 12px;
    }

    .ql-toolbar .ql-stroke {
        stroke: var(--color-card-text);
    }

    .ql-toolbar .ql-fill {
        fill: none;
    }

    .ql-toolbar button,
    .ql-toolbar .ql-picker-label {
        color: var(--color-card-text);
    }

    .ql-toolbar button:hover,
    .ql-toolbar .ql-picker-label:hover,
    .ql-toolbar .ql-picker:hover .ql-picker-label {
        color: var(--color-brand-green) !important;
    }

    .ql-toolbar button:hover .ql-stroke,
    .ql-toolbar button:hover .ql-fill,
    .ql-toolbar .ql-picker-label:hover .ql-stroke,
    .ql-toolbar .ql-picker-label:hover .ql-fill,
    .ql-toolbar .ql-picker:hover .ql-picker-label .ql-stroke,
    .ql-toolbar .ql-picker:hover .ql-picker-label .ql-fill {
        stroke: var(--color-brand-green) !important;
        fill: none;
    }

    .ql-toolbar button.ql-underline .ql-fill,
    .ql-toolbar .ql-picker.ql-list .ql-picker-item .ql-fill,
    .ql-toolbar button.ql-list .ql-fill {
        fill: var(--color-card-text) !important;
    }

    .ql-toolbar button.ql-underline:hover .ql-fill,
    .ql-toolbar button.ql-underline.ql-active .ql-fill,
    .ql-toolbar button.ql-list:hover .ql-fill,
    .ql-toolbar button.ql-list.ql-active .ql-fill,
    .ql-toolbar .ql-picker.ql-list .ql-picker-item:hover .ql-fill,
    .ql-toolbar .ql-picker.ql-list .ql-picker-item.ql-selected .ql-fill {
        fill: var(--color-brand-green) !important;
    }

    .ql-toolbar button.ql-active .ql-stroke,
    .ql-toolbar button.ql-active .ql-fill,
    .ql-toolbar .ql-picker-label.ql-active .ql-stroke,
    .ql-toolbar .ql-picker-label.ql-active .ql-fill,
    .ql-toolbar .ql-picker-item.ql-selected {
        stroke: var(--color-brand-green) !important;
        fill: none !important;
    }

    .ql-toolbar button.ql-active,
    .ql-toolbar .ql-picker-label.ql-active {
        color: var(--color-brand-green) !important;
    }

    .ql-toolbar .ql-picker {
        color: var(--color-card-text);
        height: 32px;
    }

    .ql-toolbar .ql-picker.ql-size,
    .ql-toolbar .ql-picker.ql-header {
        display: none !important;
    }

    .ql-toolbar .ql-picker-label {
        border: 1px solid var(--color-editor-divider);
        border-radius: 12px;
        padding: 6px 12px;
        height: 32px;
        display: flex;
        align-items: center;
        background: var(--color-body) !important;
    }

    .ql-toolbar .ql-picker.ql-expanded .ql-picker-label {
        border-color: var(--color-brand-green);
        background: var(--color-body) !important;
        color: var(--color-brand-green) !important;
    }

    .ql-toolbar .ql-picker.ql-expanded .ql-picker-label .ql-fill,
    .ql-toolbar .ql-picker.ql-expanded .ql-picker-label .ql-stroke {
        fill: none !important;
        stroke: var(--color-brand-green) !important;
    }

    .ql-toolbar .ql-picker-options {
        background: var(--color-body);
        border: 1px solid var(--color-editor-divider);
        border-radius: 6px;
        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        padding: 4px;
        margin-top: 4px;
        z-index: 9998;
    }

    .ql-toolbar .ql-picker-item {
        padding: 10px 12px;
        border-radius: 6px;
        color: var(--color-card-text);
    }

    .ql-toolbar .ql-picker-item:hover {
        background: var(--color-select-hover-bg);
        color: var(--color-brand-green) !important;
    }

    .ql-toolbar .ql-picker-item.ql-selected {
        background: var(--color-select-selected-bg);
        color: var(--color-brand-green) !important;
    }

    .ql-tooltip {
        background: var(--color-body) !important;
        border: 1px solid var(--color-editor-divider) !important;
        border-radius: 12px !important;
        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
        padding: 8px 12px !important;
        color: var(--color-card-text) !important;
        z-index: 9999 !important;
        transform: translate(100px, 10px) !important;
    }

    .ql-tooltip.ql-flip {
        transform: translate(100px, -10px) !important;
    }

    .ql-tooltip::before {
        content: 'Ссылка:' !important;
        margin-right: 8px;
        color: var(--color-card-subtitle);
    }

    .ql-tooltip[data-mode='link']::before {
        content: 'Введите ссылку:' !important;
    }

    .ql-tooltip a {
        color: var(--color-brand-green) !important;
    }

    .ql-tooltip a:hover {
        opacity: 0.9;
    }

    .ql-tooltip a.ql-preview {
        color: var(--color-brand-green);
    }

    .ql-tooltip a.ql-action::after {
        border-right: 1px solid var(--color-editor-divider);
        content: 'Изменить';
        margin-left: 12px;
        padding-right: 8px;
        color: var(--color-brand-green);
    }

    .ql-tooltip a.ql-remove {
        color: var(--color-danger) !important;
    }

    .ql-tooltip a.ql-remove::before {
        content: 'Удалить';
        margin-left: 8px;
        color: var(--color-danger) !important;
    }

    .ql-tooltip input[type='text'] {
        border: 1px solid var(--color-editor-divider) !important;
        border-radius: 6px !important;
        padding: 8px 12px !important;
        font-size: 14px !important;
        color: var(--color-card-text) !important;
        background: var(--color-body) !important;
    }

    .ql-tooltip input[type='text']:focus {
        border-color: var(--color-brand-green) !important;
        outline: none !important;
    }

    .ql-snow .ql-editor.ql-blank::before {
        color: var(--color-card-subtitle) !important;
        font-style: normal;
        opacity: 1;
    }

    .ql-snow .ql-editor a {
        color: var(--color-brand-green);
        font-weight: bold;
        text-decoration: underline;
    }

    .ql-snow .ql-editor h1 {
        font-size: 2em;
        font-weight: 700;
        margin: 0.67em 0;
    }

    .ql-snow .ql-editor h2 {
        font-size: 1.5em;
        font-weight: 600;
        margin: 0.75em 0;
    }

    .ql-snow .ql-editor h3 {
        font-size: 1.17em;
        font-weight: 600;
        margin: 0.83em 0;
    }

    .ql-snow .ql-editor blockquote {
        border-left: 4px solid var(--color-brand-green);
        margin: 1em 0;
        padding-left: 1em;
        color: var(--color-card-subtitle);
    }

    .ql-snow .ql-editor ul {
        list-style-type: disc;
        padding-left: 2em;
    }

    .ql-snow .ql-editor ol {
        list-style-type: decimal;
        padding-left: 2em;
    }
`;

/** Визуально и по pointer-events отключает тулбар; фон области текста — «неактивный». */
export const quillDisabledStyle = css`
    .ql-toolbar.ql-snow {
        pointer-events: none;
        opacity: 0.55;
        cursor: not-allowed;
    }

    .ql-container .ql-editor {
        background: hsl(var(--muted) / 0.45);
        cursor: default;
    }

    .ql-container .ql-editor.ql-blank::before {
        color: var(--color-card-subtitle) !important;
    }
`;

export const quillEditorStyle = css`
    background: var(--color-body);
    border: 1px solid var(--color-border-layout);
    border-radius: 12px;
    overflow: hidden;
    width: 100%;
    max-width: 100%;

    .ql-toolbar.ql-snow {
        border: none !important;
        border-bottom: 1px solid var(--color-editor-divider) !important;
    }

    .ql-container.ql-snow {
        border: none !important;
    }
`;

export const quillEditorDisabledStyle = css`
    border-color: hsl(var(--muted-foreground) / 0.25);
`;
